import ReactiveDialog from "../../common/ReactiveDialog";
import {
  Box,
  ListSubheader,
  Typography,
  FormControlLabel,
  RadioGroup,
} from "@mui/material";
import {
  useChangeFamilyPlan,
  useGetFamilyDetails,
} from "../../../services/advisor";
import Loading from "../../common/Loading";
import { useContext, useEffect, useState } from "react";
import { BillingProduct } from "protogen/billing_pb";
import { useListBillingProducts } from "../../../services/billing";
import { MenuItem } from "@mui/material";
import Select from "components/common/Select";
import Checkbox from "components/common/Checkbox";
import Radio from "components/common/Radio";
import { PlanUtilization } from "protogen/common_pb";
import { Family } from "protogen/advisors_service_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";

type Props = {
  familyRef: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onUpdate: (family: Family) => void;
  mode?: "change" | "pause";
};

type PlanChangeTiming = "" | "scheduled" | "immediate";

const PAUSE_PRODUCT_NAME = "Service Pause";

const isUpgradeSelection = (
  currentProduct?: BillingProduct,
  selectedProduct?: BillingProduct,
) =>
  !!currentProduct &&
  !!selectedProduct &&
  selectedProduct.amountCents > currentProduct.amountCents;

const immediateChargeAmount = (
  currentProduct?: BillingProduct,
  selectedProduct?: BillingProduct,
) => {
  if (!currentProduct || !selectedProduct) return 0;
  if (currentProduct.name === PAUSE_PRODUCT_NAME) {
    return selectedProduct.amountCents / 100;
  }
  return (
    Math.abs(selectedProduct.amountCents - currentProduct.amountCents) / 100
  );
};

const ChangePlanContent = ({
  currentProduct,
  selectedProduct,
  onProductChange,
  disabled = false,
  planUtilization,
  isAdmin,
  confirmed,
  onConfirmationChange,
  mode = "change",
  timing,
  onTimingChange,
}: {
  currentProduct?: BillingProduct;
  selectedProduct?: BillingProduct;
  onProductChange: (product: BillingProduct) => void;
  disabled?: boolean;
  planUtilization?: PlanUtilization;
  isAdmin: boolean;
  confirmed: boolean;
  onConfirmationChange: (confirmed: boolean) => void;
  mode?: "change" | "pause";
  timing: PlanChangeTiming;
  onTimingChange: (timing: PlanChangeTiming) => void;
}) => {
  const { request, data, loading } = useListBillingProducts();

  useEffect(() => {
    if (!data) {
      request();
    }
  }, []);

  const pauseProductId = "prod_TUnvOxdpan1AYN"; // Service Pause

  useEffect(() => {
    if (mode === "pause" && data?.products) {
      const pauseProduct = data.products.find((p) => p.id === pauseProductId);
      if (pauseProduct) {
        onProductChange(pauseProduct);
      }
    }
  }, [mode, data?.products]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <Loading />
      </Box>
    );
  }

  const products = data?.products || [];
  const mainOfferings = new Set([
    // Prod.
    "prod_QGpeOl3cDuxaiZ", // Total Game Changer
    "prod_QKBrlo0j4DuINP", // Free Time Finder
    "prod_QTbbvlRkHU1GKk", // Peace of Mind
    "prod_TUnvOxdpan1AYN", // Service Pause
    "prod_Siu5SWO7LgQyV3", // Pay as you go
    // "prod_TMYk6KqVdwcK5D", // Peace of Mind (higher price)
    // "prod_TMYhrOnSaKy921", // Free Time Finder (higher price)
    "prod_TOodG8twlbPm7H", // Game changer
    // "prod_TMYm03JfUezhCE", // Game Changer (higher price)
    "prod_TMYnSweTwZn6Jx", // Business Pro

    // Dev.
    "prod_QZAxZCP1JFyGGW",
    "prod_QZAx7GkKumuhHy",
    "prod_QZAvacnqgiPvls",
  ]);

  const nextBillingDateStr = new Date(
    planUtilization?.periodEndDate || 0,
  ).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  const isUpgrade = isUpgradeSelection(currentProduct, selectedProduct);
  const isFromPause = currentProduct?.name === PAUSE_PRODUCT_NAME;
  const chargeAmount = immediateChargeAmount(currentProduct, selectedProduct);

  const ProductMenuItem = (product: BillingProduct) => (
    <MenuItem
      key={product.id}
      value={product.id}
      disabled={product.id === currentProduct?.id}
    >
      <div>
        <Typography variant="subtitle1">
          <span style={{ fontWeight: "600", marginRight: "6px" }}>
            ${`${(product.amountCents / 100).toFixed(2)}`}/mo
          </span>
          {product.name}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {product.description}
        </Typography>
      </div>
    </MenuItem>
  );

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      {mode === "change" ? (
        <>
          <Typography variant="body" sx={{ fontStyle: "italic" }}>
            Need to temporarily pause service? Select a pause plan to maintain
            the relationship while billing is paused. The family can resume
            their regular plan at any time.
          </Typography>
          <Typography variant="bodyHeavy">
            Select the new plan for this family
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body">
            Pausing service will maintain the relationship while billing is
            paused. This change will take effect on the next billing date,{" "}
            <strong>{nextBillingDateStr}</strong>.
          </Typography>
        </>
      )}
      {mode === "change" && (
        <Select
          variant="outlined"
          placeholder="Service tier"
          displayEmpty
          disabled={disabled}
          defaultValue={currentProduct?.id || ""}
          onChange={(e) => {
            const val = e.target.value;
            const product = products.find((p) => p.id === val);
            if (product) {
              onProductChange(product);
            }
          }}
          sx={{
            "& .MuiInputBase-input": {
              marginTop: "0px !important",
            },
            width: "100%",
            height: "60px",
          }}
        >
          <MenuItem key={""} value={""} disabled>
            Service tier
          </MenuItem>
          {products
            .filter((p) => mainOfferings.has(p.id))
            .sort((a, b) => a.amountCents - b.amountCents)
            .map(ProductMenuItem)}
          {isAdmin && <ListSubheader>Additional Offerings</ListSubheader>}
          {(isAdmin ? products : [])
            .filter((p) => !mainOfferings.has(p.id))
            .sort((a, b) => a.amountCents - b.amountCents)
            .map(ProductMenuItem)}
        </Select>
      )}
      {mode === "change" && (
        // Height is reserved for the two-option layout so revealing the
        // immediate-upgrade option never shifts the dialog's action buttons.
        <Box sx={{ minHeight: "116px" }}>
          <Typography variant="bodyHeavy">
            When should this take effect?
          </Typography>
          <RadioGroup
            value={timing}
            onChange={(e) => onTimingChange(e.target.value as PlanChangeTiming)}
          >
            <FormControlLabel
              value="scheduled"
              disabled={disabled}
              control={<Radio />}
              label={`On the next billing date — ${nextBillingDateStr}`}
            />
            {isUpgrade && (
              <FormControlLabel
                value="immediate"
                disabled={disabled}
                control={<Radio />}
                label={
                  isFromPause
                    ? `Immediately — charge $${chargeAmount.toFixed(
                        2,
                      )} today and restart the billing cycle`
                    : `Immediately — charge $${chargeAmount.toFixed(2)} today`
                }
              />
            )}
          </RadioGroup>
        </Box>
      )}
      {!isAdmin && (
        <FormControlLabel
          control={
            <Checkbox
              checked={confirmed}
              onChange={(e) => onConfirmationChange(e.target.checked)}
            />
          }
          label="I confirm these changes have been communicated with the client"
        />
      )}
    </Box>
  );
};

export default ({
  familyRef,
  open,
  setOpen,
  onUpdate,
  mode = "change",
}: Props) => {
  const {
    data: familyData,
    request: requestFamily,
    loading: loadingFamily,
  } = useGetFamilyDetails();
  const [selectedProduct, setSelectedProduct] = useState<BillingProduct>();
  const [confirmed, setConfirmed] = useState(false);
  const [timing, setTiming] = useState<PlanChangeTiming>("scheduled");
  const { request: requestChangePlan, loading: loadingChangePlan } =
    useChangeFamilyPlan();
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);

  useEffect(() => {
    if (open) {
      requestFamily({ familyRef });
      setConfirmed(false);
      setSelectedProduct(undefined);
      setTiming("scheduled");
    }
  }, [open, familyRef]);

  const currentProduct = familyData?.family?.billingInfo?.product;

  const onPlanChange = async () => {
    if (!selectedProduct || !familyData?.family) return;
    const resp = await requestChangePlan({
      familyRef,
      product: selectedProduct,
      applyImmediately: timing === "immediate",
    });
    if (resp?.family) {
      onUpdate(resp.family);
    }
  };

  return (
    <ReactiveDialog
      open={open}
      onClose={() => setOpen(false)}
      title={mode === "pause" ? "Pause family service" : "Change family plan"}
      primaryActionName={mode === "pause" ? "Pause" : "Change Plan"}
      primaryActionEnabled={
        !loadingChangePlan &&
        !loadingFamily &&
        !!familyData?.family &&
        !!selectedProduct?.id &&
        selectedProduct?.id !== familyData?.family.billingInfo?.product?.id &&
        timing !== "" &&
        (isAdmin || mode === "pause" || confirmed)
      }
      primaryAction={onPlanChange}
      fullWidthSize="sm"
    >
      {loadingFamily ? (
        <Box display="flex" justifyContent="center" p={3}>
          <Loading />
        </Box>
      ) : familyData?.family ? (
        <ChangePlanContent
          disabled={loadingChangePlan}
          currentProduct={currentProduct}
          selectedProduct={selectedProduct}
          onProductChange={(product) => {
            setSelectedProduct(product);
            const requiresTimingChoice =
              mode === "change" && isUpgradeSelection(currentProduct, product);
            setTiming(requiresTimingChoice ? "" : "scheduled");
          }}
          planUtilization={familyData.planUtilization}
          isAdmin={isAdmin}
          confirmed={confirmed}
          onConfirmationChange={setConfirmed}
          mode={mode}
          timing={timing}
          onTimingChange={setTiming}
        />
      ) : null}
    </ReactiveDialog>
  );
};
